import { breadcrumbSchema } from "@/lib/schema";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { SiteLayout } from "@/components/site/Layout";
import { trackFormView } from "@/lib/analytics";
import { useGhlSubmitConfirm } from "@/lib/confirm-submission";

const FORM_ID = "k8voTA3U8DlaGhEKlC5L";
const FORM_SRC = `https://link.agent-moves.com/widget/form/${FORM_ID}`;
const SCRIPT_SRC = "https://link.agent-moves.com/js/form_embed.js";

export const Route = createFileRoute("/kcn-join-our-team")({
  head: () => ({
    meta: [
      { title: "Real Estate Teams Hiring - Request a KCN Invitation" },
      {
        name: "description",
        content:
          "Real estate teams hiring top producers: request your invitation to the Kristan Cole Network Round Table. Real estate agent recruiting, by invitation only.",
      },
      { property: "og:title", content: "Request a Round Table Invitation - Kristan Cole Network" },
      { property: "og:url", content: "https://joinkcn.com/kcn-join-our-team" },
      {
        property: "og:description",
        content:
          "Join Kristan Cole's private Monthly Market Round Table. Real market intelligence, proven scripts, and conversations that win clients.",
      },
    ],
    links: [{ rel: "canonical", href: "https://joinkcn.com/kcn-join-our-team" }],
    scripts: [breadcrumbSchema([{ name: "Request an Invitation", path: "/kcn-join-our-team" }])],
  }),
  component: RequestInvitePage,
});

function RequestInvitePage() {
  useGhlSubmitConfirm("round_table", "/kcn-join-our-team");
  useEffect(() => {
    trackFormView("round_table", "/kcn-join-our-team");
    if (!document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="container-edge pt-32 md:pt-40 pb-12">
          <p className="eyebrow text-foreground/70 mb-6">Invitation Only · Monthly Market Round Table</p>
        </div>
        <div className="container-edge">
          <div className="grid lg:grid-cols-12 gap-0">
            {/* Framing copy */}
            <div className="lg:col-span-5 bg-black text-white pr-0 md:pr-8 lg:pr-12 pt-0 pb-16 lg:pb-24">
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                Your market knowledge sets you <span className="serif-italic text-white/90">apart.</span>
              </h1>
              <p className="font-display italic text-2xl md:text-3xl mt-5 text-white/90 leading-snug">
                A private monthly session with Kristan and her top producers.
              </p>
              <div className="mt-8 space-y-5 text-white/85 text-base md:text-lg leading-relaxed">
                <p>
                  Each month we break down what's really happening in the
                  market - interest rates, scripts that win listings, the four
                  options today's sellers actually have, and the AI-driven
                  tactics getting agents found and chosen.
                </p>
                <p>
                  For the first time, we're opening the door for you to join us.
                </p>
                <p className="font-display italic text-white/90">
                  Share a few details below. Seats are limited and invitations are extended based on fit and availability.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7 bg-black lg:pl-12">
              <iframe
                src={FORM_SRC}
                style={{ width: "100%", border: "none", display: "block" }}
                id={`inline-${FORM_ID}`}
                data-layout='{"id":"INLINE"}'
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Request Roundtable Invite"
                data-height="700"
                data-layout-iframe-id={`inline-${FORM_ID}`}
                data-form-id={FORM_ID}
                title="Request Roundtable Invite"
                height={700}
                className="min-h-[600px] md:min-h-[700px] w-full"
              />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
